import { Button } from "@hanjan-shieo/ui";

export const routeContracts = [
  { pattern: "/", label: "게임 추천" },
  { pattern: "/session", label: "한 기기 모임 진행" },
  { pattern: "/meetups", label: "모임 찾기" },
  { pattern: "/meetups/new", label: "모임 만들기" },
  { pattern: "/meetups/:id", label: "모임 상세" },
  { pattern: "/meetups/:id/chat", label: "임시 그룹채팅" },
  { pattern: "/my-meetups", label: "내 모임" },
  { pattern: "/moderation", label: "신고 운영" },
  { pattern: "/places", label: "한잔 코스" },
  { pattern: "/recover", label: "회복 가이드" },
  { pattern: "/age", label: "성인 확인" },
  { pattern: "/games", label: "게임 전체" },
  { pattern: "/random", label: "랜덤 게임" },
  { pattern: "/discover", label: "낯선 게임 탐험" },
  { pattern: "/games/:slug", label: "게임 규칙" },
  { pattern: "/play/local", label: "한 기기 플레이" },
  { pattern: "/room/new", label: "방 만들기" },
  { pattern: "/room/join", label: "방 참여" },
  { pattern: "/room/:id", label: "공유 방" },
  { pattern: "/queue", label: "게임 순서" },
  { pattern: "/recap", label: "게임 마무리 기록" },
  { pattern: "/presets", label: "모임 설정" },
  { pattern: "/settings", label: "설정" },
  { pattern: "/safety", label: "안전 약속" },
  { pattern: "/privacy", label: "개인정보 안내" },
  { pattern: "/terms", label: "이용약관" },
  { pattern: "/support", label: "문의와 제보" },
  { pattern: "/purchase", label: "호스트 패스" },
  { pattern: "/purchase/history", label: "구매 복원" },
] as const;

export type RouteStateKind = "loading" | "empty" | "error" | "offline";
export type RouteStateFixture = {
  id: string;
  route: typeof routeContracts[number]["pattern"];
  routeLabel: string;
  state: RouteStateKind;
  title: string;
  body: string;
  action: string;
};

const stateCopy: Record<RouteStateKind, (label: string) => Pick<RouteStateFixture, "title" | "body" | "action">> = {
  loading: (label) => ({ title: `${label}을 준비하고 있어요`, body: "현재 내용을 바꾸지 않고 필요한 정보만 확인합니다.", action: "잠시만 기다리기" }),
  empty: (label) => ({ title: `${label}에 아직 보여 줄 내용이 없어요`, body: "조건을 하나 풀거나 안전한 기본 흐름으로 돌아갈 수 있습니다.", action: "기본 흐름으로 돌아가기" }),
  error: (label) => ({ title: `${label}을 열지 못했어요`, body: "입력한 내용은 이 기기에 남아 있습니다. 가짜 성공으로 넘기지 않고 다시 시도할 수 있어요.", action: "다시 시도" }),
  offline: (label) => ({ title: "지금은 오프라인이에요", body: `${label}의 기기 기능은 계속 사용할 수 있고, 서버가 필요한 동작은 연결 뒤 사용자가 다시 확인합니다.`, action: "이 기기에서 계속하기" }),
};

export const routeStateFixtures: RouteStateFixture[] = routeContracts.flatMap((route) =>
  (["loading", "empty", "error", "offline"] as const).map((state) => ({
    id: `${route.pattern}:${state}`,
    route: route.pattern,
    routeLabel: route.label,
    state,
    ...stateCopy[state](route.label),
  })),
);

export function routePattern(pathname: string): RouteStateFixture["route"] {
  if (/^\/meetups\/[^/]+\/chat$/.test(pathname)) return "/meetups/:id/chat";
  if (/^\/meetups\/[^/]+$/.test(pathname) && pathname !== "/meetups/new") return "/meetups/:id";
  if (pathname.startsWith("/games/") && pathname !== "/games/") return "/games/:slug";
  if (pathname.startsWith("/room/") && !["/room/new", "/room/join"].includes(pathname)) return "/room/:id";
  return routeContracts.find((route) => route.pattern === pathname)?.pattern ?? "/";
}

export function getRouteStateFixture(pathname: string, state: RouteStateKind) {
  const pattern = routePattern(pathname);
  return routeStateFixtures.find((fixture) => fixture.route === pattern && fixture.state === state)!;
}

export function RouteStateView({ pathname, state, onAction }: { pathname: string; state: RouteStateKind; onAction?: () => void }) {
  const fixture = getRouteStateFixture(pathname, state);
  const busy = state === "loading";
  return <section className={`route-state route-state--${state}`} data-route-state={`${fixture.route}:${state}`} aria-busy={busy || undefined} role={state === "error" ? "alert" : "status"}>
    <span className="route-state__symbol" aria-hidden="true">{state === "loading" ? "•••" : state === "empty" ? "◇" : state === "error" ? "!" : "↯"}</span>
    <div><span className="eyebrow">{fixture.routeLabel} · {state}</span><h1>{fixture.title}</h1><p>{fixture.body}</p></div>
    {state !== "loading" && <Button onClick={onAction}>{fixture.action}</Button>}
  </section>;
}
